import { Loader2 } from 'lucide-react';
import { getNetworkDisplayName, NetworkType } from '@mls-wallet/shared-types';
import { NetworkIcon } from '@/components/shared/network-icon';
import { formatCurrency } from '@/lib/utils';

/**
 * Props for the TransactionBuilderNetworkCard component
 */
interface TransactionBuilderNetworkCardProps {
  networkId: NetworkType;
  usdValue: number;
  isLoading?: boolean;
  onClick?: () => void;
  className?: string;
}

/**
 * Network card component for the transaction builder
 * Displays network information with icon, name, and USD value
 * Matches the Figma design for network selection
 */
export function TransactionBuilderNetworkCard({
  networkId,
  usdValue,
  isLoading = false,
  onClick,
  className = '',
}: TransactionBuilderNetworkCardProps) {
  return (
    <button
      className={`
        bg-neutral-50
        dark:bg-neutral-900
        border border-solid border-white
        dark:border-zinc-800
        rounded-[15px]
        shadow-[0px_4px_6px_-3px_rgba(0,0,0,0.1)]
        w-full
        cursor-pointer
        hover:bg-slate-100
        dark:hover:bg-slate-800
        transition-colors
        relative
        my-[12px]
        ${className}
      `}
      onClick={onClick}
    >
      <div className="overflow-clip relative rounded-[inherit] size-full py-[30px]">
        {/* Network name and icon on the left */}
        <div className="absolute left-[12px] top-1/2 -translate-y-1/2 flex items-center gap-[10px]">
          <NetworkIcon networkId={networkId} size="md" />
          <p
            className="text-black dark:text-white font-medium not-italic"
            style={{
              fontFamily: 'Inter, sans-serif',
              fontSize: '20px',
              lineHeight: 'normal',
            }}
          >
            {getNetworkDisplayName(networkId)}
          </p>
        </div>

        {/* USD value on the right */}
        <div className="absolute right-[12px] top-1/2 -translate-y-1/2 text-right text-zinc-600 dark:text-zinc-400 font-normal not-italic min-w-[96px] flex justify-end">
          {isLoading ? (
            <Loader2 className="h-5 w-5 animate-spin" />
          ) : (
            <p
              style={{
                fontFamily: 'Inter, sans-serif',
                fontSize: '20px',
                lineHeight: 'normal',
              }}
            >
              {formatCurrency(usdValue, {
                minimumFractionDigits: 2,
              })}
            </p>
          )}
        </div>
      </div>
    </button>
  );
}
